import { IntlShape } from 'react-intl'

import {
  NewPaymentFormQuery,
  OrderingType,
  User,
  VenueFieldRequirement,
} from '@/gql/graphql'

import {
  PaymentMethodKey,
  PaymentMethodValue,
  POST_PAID_PROCESSOR_TYPES,
} from './hooks/usePaymentFormMethods'
import { getPaymentSchema } from './PaymentSchema'

export const getValidationSchema = (
  {
    orderingType,
    showEmailFieldWhenDineIn,
    venue,
    isNewGroupOrder,
    isEonxEnv,
  }: {
    orderingType: OrderingType
    showEmailFieldWhenDineIn: boolean
    venue: NewPaymentFormQuery['guestVenue']
    isNewGroupOrder?: boolean
    isEonxEnv: boolean | null
  },
  intl: IntlShape,
) => {
  const orderingTypeSettings = venue?.orderingTypes.find(
    (setting) => setting.orderingType === orderingType,
  )

  const emailFieldRequirement =
    orderingTypeSettings?.emailFieldRequirement ||
    VenueFieldRequirement.Optional
  const coverCountFieldRequirement =
    orderingTypeSettings?.coverCountFieldRequirement ||
    VenueFieldRequirement.Hidden

  return getPaymentSchema(
    {
      venue: { ageRestrictionText: venue?.ageRestrictionText },
      hasOptions: !!(
        orderingTypeSettings?.options &&
        orderingTypeSettings?.options.length > 0
      ),
      orderingType,
      emailFieldRequirement,
      coverCountFieldRequirement,
      showEmailFieldWhenDineIn,
      isNewGroupOrder,
      isEonxEnv,
    },
    intl,
  )
}

export const getSubmitButtonLabel = (
  {
    paymentMethod,
    venue,
    totalInCents,
    isNew,
    isNewGroupOrder,
  }: {
    paymentMethod: PaymentMethodValue
    venue: NonNullable<NewPaymentFormQuery['guestVenue']>
    totalInCents: number
    isNew: boolean
    isNewGroupOrder?: boolean
  },
  intl: IntlShape,
) => {
  if (isNewGroupOrder) {
    return intl.formatMessage({
      defaultMessage: 'Start group tab',
      id: 'qjvbmX',
    })
  }
  // Must be first as its not a "real payment method"
  if (totalInCents === 0) {
    return intl.formatMessage({ defaultMessage: 'Place order', id: 'JItzoH' })
  }

  if (paymentMethod) {
    if (paymentMethod.key.match(PaymentMethodKey.StripeNewCard) && !isNew) {
      return intl.formatMessage({
        defaultMessage: 'Add a new card',
        id: 'lrfCD6',
      })
    }

    if (paymentMethod.key.match(PaymentMethodKey.TabNew)) {
      return intl.formatMessage({ defaultMessage: 'Continue', id: 'acrOoz' })
    }

    if (POST_PAID_PROCESSOR_TYPES.includes(paymentMethod.processorType)) {
      return (
        venue.paymentProcessor?.unpaidProcessorTypeLabel ||
        intl.formatMessage({ defaultMessage: 'Pay now', id: '7tOz+m' })
      )
    }

    if (paymentMethod.key.match(PaymentMethodKey.ZeroAmount)) {
      return intl.formatMessage({ defaultMessage: 'Place order', id: 'JItzoH' })
    }
  }

  return intl.formatMessage({ defaultMessage: 'Pay now', id: '7tOz+m' })
}

// https://stripe.com/docs/statement-descriptors#requirements
// Duplicate from stringUtils back-end
export const stripeStatementDescriptor = (name: string) => {
  return name
    .replace(/[^A-Za-z0-9\s]/g, '')
    .trim()
    .replace(/\s\s/g, ' ')
    .substring(0, 22)
    .toUpperCase()
}

export const isAnonymousGuest = (user?: Pick<User, 'mobile'>): boolean => {
  return user?.mobile?.startsWith('+69') ?? false
}
